import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, ChevronRight } from 'lucide-react';

// Separadores data - Spain only
const SEPARADORES_DATA = [
  { nombre: 'CELOSIA H-080 (A)', altura: 80, longitud: '6 m', unidadesPaq: 60 },
  { nombre: 'CELOSIA H-100 (A)', altura: 100, longitud: '6 m', unidadesPaq: 80 },
  { nombre: 'CELOSIA H-120 (A)', altura: 120, longitud: '6 m', unidadesPaq: 80 },
  { nombre: 'CELOSIA H-140 (A)', altura: 140, longitud: '6 m', unidadesPaq: 80 },
  { nombre: 'CELOSIA H-170 (A)', altura: 170, longitud: '6 m', unidadesPaq: 100 },
  { nombre: 'CELOSIA H-200 (A)', altura: 200, longitud: '6 m', unidadesPaq: 100 },
  { nombre: 'CELOSIA H-230 (A)', altura: 230, longitud: '6 m', unidadesPaq: 80 },
];

// Country configuration for Separadores
const COUNTRY_CONFIG = {
  ES: {
    code: 'ES',
    name: { en: 'Spain', es: 'España', fr: 'Espagne', de: 'Spanien', nl: 'Spanje' },
    mapImage: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9a/EU-Spain.svg/1200px-EU-Spain.svg.png',
  },
};

// Translations
const translations = {
  en: {
    pageTitle: 'Separators',
    pageSubtitle: 'Separators and lattice girders for reinforced concrete',
    backToProducts: 'Back to Products',
    selectCountry: 'Select your market',
    viewProducts: 'View Products',
    productTable: 'Product Specifications',
    tableHeaders: {
      nombre: 'Name',
      altura: 'Height (mm)',
      longitud: 'Length',
      unidadesPaq: 'Units/Pack',
    },
    downloadPdf: 'Download PDF',
    pdfPlaceholder: 'PDF coming soon',
    availableIn: 'Available in',
  },
  es: {
    pageTitle: 'Separadores',
    pageSubtitle: 'Separadores y celosías para hormigón armado',
    backToProducts: 'Volver a Productos',
    selectCountry: 'Selecciona tu mercado',
    viewProducts: 'Ver Productos',
    productTable: 'Especificaciones de Producto',
    tableHeaders: {
      nombre: 'Nombre',
      altura: 'Altura (mm)',
      longitud: 'Longitud',
      unidadesPaq: 'Uds/Paq',
    },
    downloadPdf: 'Descargar PDF',
    pdfPlaceholder: 'PDF próximamente',
    availableIn: 'Disponible en',
  },
  fr: {
    pageTitle: 'Séparateurs',
    pageSubtitle: 'Séparateurs et treillis pour béton armé',
    backToProducts: 'Retour aux Produits',
    selectCountry: 'Sélectionnez votre marché',
    viewProducts: 'Voir les Produits',
    productTable: 'Spécifications Produit',
    tableHeaders: {
      nombre: 'Nom',
      altura: 'Hauteur (mm)',
      longitud: 'Longueur',
      unidadesPaq: 'Unités/Paq',
    },
    downloadPdf: 'Télécharger PDF',
    pdfPlaceholder: 'PDF bientôt disponible',
    availableIn: 'Disponible en',
  },
  de: {
    pageTitle: 'Abstandhalter',
    pageSubtitle: 'Abstandhalter und Gitterträger für Stahlbeton',
    backToProducts: 'Zurück zu Produkten',
    selectCountry: 'Wählen Sie Ihren Markt',
    viewProducts: 'Produkte Anzeigen',
    productTable: 'Produktspezifikationen',
    tableHeaders: {
      nombre: 'Name',
      altura: 'Höhe (mm)',
      longitud: 'Länge',
      unidadesPaq: 'Stk/Pkg',
    },
    downloadPdf: 'PDF Herunterladen',
    pdfPlaceholder: 'PDF demnächst verfügbar',
    availableIn: 'Verfügbar in',
  },
  nl: {
    pageTitle: 'Afstandhouders',
    pageSubtitle: 'Afstandhouders en vakwerkliggers voor gewapend beton',
    backToProducts: 'Terug naar Producten',
    selectCountry: 'Selecteer uw markt',
    viewProducts: 'Bekijk Producten',
    productTable: 'Productspecificaties',
    tableHeaders: {
      nombre: 'Naam',
      altura: 'Hoogte (mm)',
      longitud: 'Lengte',
      unidadesPaq: 'Stuks/Pak',
    },
    downloadPdf: 'PDF Downloaden',
    pdfPlaceholder: 'PDF binnenkort beschikbaar',
    availableIn: 'Beschikbaar in',
  },
};

// Country Block Component
const CountryBlock = ({ country, isSelected, onClick, t, language }) => {
  const countryName = country.name[language] || country.name.en;
  
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between p-4 border-2 transition-all ${
        isSelected 
          ? 'border-green-600 bg-green-50' 
          : 'border-slate-200 bg-white hover:border-green-400 hover:bg-slate-50'
      }`}
      data-testid={`country-block-${country.code}`}
    >
      <div className="flex items-center space-x-4">
        {/* Country Map Silhouette */}
        <div className="w-16 h-12 bg-slate-100 rounded overflow-hidden flex items-center justify-center">
          <img 
            src={country.mapImage} 
            alt={countryName}
            className="w-full h-full object-contain p-1"
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = document.createElement('span');
              fallback.className = 'text-2xl font-bold text-slate-400';
              fallback.textContent = country.code;
              e.target.parentElement.appendChild(fallback);
            }}
          />
        </div>
        <div className="text-left">
          <h3 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-tight">
            {countryName}
          </h3>
        </div>
      </div>
      <ChevronRight className={`h-5 w-5 transition-transform ${isSelected ? 'rotate-90 text-green-600' : 'text-slate-400'}`} />
    </button>
  );
};

// Main Page Component
export const Separadores = () => {
  const { language } = useLanguage();
  const [selectedCountry, setSelectedCountry] = useState('ES'); // Default to Spain
  
  const t = translations[language] || translations.en;

  return (
    <div className="min-h-screen bg-slate-900 pt-16 md:pt-20" data-testid="separadores-page">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/products" 
            className="inline-flex items-center space-x-2 text-green-500 hover:text-green-400 mb-6 transition-colors"
            data-testid="back-to-products"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-medium">{t.backToProducts}</span>
          </Link>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-4">
            {t.pageTitle}
          </h1>
          <p className="text-slate-400 text-lg md:text-xl max-w-2xl">
            {t.pageSubtitle}
          </p>
        </div>
      </section>

      <div className="bg-slate-50">
      {/* Country Selection */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-tight mb-4">
            {t.selectCountry}
          </h2>
          <div className="max-w-md">
            <CountryBlock
              country={COUNTRY_CONFIG.ES}
              isSelected={selectedCountry === 'ES'}
              onClick={() => setSelectedCountry('ES')}
              t={t}
              language={language}
            />
          </div>
        </div>
      </section>

      {/* Product Table - Spain */}
      {selectedCountry === 'ES' && (
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-slate-200">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-8 bg-slate-100 rounded overflow-hidden flex items-center justify-center">
                  <img 
                    src={COUNTRY_CONFIG.ES.mapImage} 
                    alt="Spain"
                    className="w-full h-full object-contain p-1"
                  />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase tracking-tight">
                    {t.productTable}
                  </h2>
                  <p className="text-slate-500 text-sm">
                    {t.availableIn}: {COUNTRY_CONFIG.ES.name[language] || 'Spain'}
                  </p>
                </div>
              </div>
            </div>

            {/* Product Table */}
            <div className="bg-white border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="bg-slate-900 text-white text-xs uppercase tracking-wider">
                      <th className="p-4 text-left font-medium">{t.tableHeaders.nombre}</th>
                      <th className="p-4 text-center font-medium">{t.tableHeaders.altura}</th>
                      <th className="p-4 text-center font-medium">{t.tableHeaders.longitud}</th>
                      <th className="p-4 text-center font-medium">{t.tableHeaders.unidadesPaq}</th>
                      <th className="p-4 text-center font-medium">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SEPARADORES_DATA.map((product, idx) => (
                      <tr 
                        key={product.nombre}
                        className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-green-50 transition-colors`}
                        data-testid={`product-row-${idx}`}
                      >
                        <td className="p-4 font-mono font-bold text-slate-900">
                          {product.nombre}
                        </td>
                        <td className="p-4 text-center font-mono text-sm text-slate-700">
                          {product.altura}
                        </td>
                        <td className="p-4 text-center font-mono text-sm text-slate-700">
                          {product.longitud}
                        </td>
                        <td className="p-4 text-center font-mono text-sm text-slate-700">
                          {product.unidadesPaq}
                        </td>
                        <td className="p-4 text-center">
                          {/* PDF Placeholder */}
                          <span className="text-xs text-slate-400 italic">
                            {t.pdfPlaceholder}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Download Area Placeholder */}
            <div className="mt-6 p-6 bg-slate-100 border border-slate-200">
              <div className="flex items-center space-x-3 text-slate-500">
                <Download className="h-5 w-5" />
                <span className="text-sm">
                  {t.pdfPlaceholder}
                </span>
              </div>
            </div>
          </div>
        </section>
      )}
      </div>
    </div>
  );
};
